import React from 'react';
import { StyleSheet, Button, Text } from 'react-native';
import { Portal, Dialog } from 'react-native-paper';
import { Formik } from 'formik';
import { format, parse } from 'date-fns';
import { ActivationFieldContent } from './ActivationFieldContent';
import { getActivationValidationSchema } from './activationValidationSchema';
import { useCreateUserEventMutation, useUpdateUserMutation, } from '../services/abbiWayApi';
import { UserEventType } from '../types/userEvent.interface.d';
const daysBetween = (start, end) => {
    const startDate = parse(start, 'MM-dd-yyyy', new Date());
    const endDate = parse(end, 'MM-dd-yyyy', new Date());
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
export const ActivationDialog = ({ close, user, me }) => {
    const [createNote] = useCreateUserEventMutation();
    const [updateUser] = useUpdateUserMutation();
    return (<Portal>
      <Dialog visible={true} onDismiss={() => close()} style={styles.activateDialog}>
        <Formik initialValues={{
            lossDays: 4,
            mainteanceDays: 2,
            rewardDays: 1,
            totalDays: '', // this is just used for error reporting
            startDate: format(new Date(), 'MM-dd-yyyy'),
            endDate: '',
            plan: '',
            weekStartsOn: (user.weekStartsOn === 0 || user.weekStartsOn === 1
                ? user.weekStartsOn
                : 1),
        }} validationSchema={getActivationValidationSchema(true)} onSubmit={values => {
            const weekStartsOn = values.weekStartsOn === 0 || values.weekStartsOn === 1
                ? values.weekStartsOn
                : 1;
            const newActivation = {
                lossDays: values.lossDays,
                mainteanceDays: values.mainteanceDays,
                rewardDays: values.rewardDays,
                startDate: values.startDate,
                endDate: values.endDate,
                plan: values.plan,
                weekStartsOn,
            };
            const eventPayload = {
                eventType: UserEventType.activated,
                forUserId: user.id,
                byUserId: me.id,
                note: JSON.stringify(newActivation),
            };
            const userUpdatePayload = {
                programType: `${newActivation.lossDays}${newActivation.mainteanceDays}${newActivation.rewardDays}`,
                weekStartsOn,
            };
            createNote(eventPayload).then(() => updateUser({ payload: userUpdatePayload, userId: user.id }));
        }}>
          {({ handleSubmit, setTouched, validateForm, errors, values }) => {
            return (<>
                <Dialog.Title>{`Activating ${user?.firstName} ${user?.lastName}`}</Dialog.Title>
                <Dialog.Content>
                  <ActivationFieldContent width={500} errors={errors} showWeekStartsOn duration={(() => {
                    const isValid = !errors.startDate &&
                        !errors.endDate &&
                        values.startDate &&
                        values.endDate;
                    return isValid
                        ? daysBetween(values.startDate, values.endDate)
                        : undefined;
                })()} readOnly={false}/>
                </Dialog.Content>
                <Dialog.Actions style={styles.buttons}>
                  <Button title="Cancel" onPress={() => close()}/>
                  <Button title="Activate" onPress={async () => {
                    const validationErrors = await validateForm();
                    if (Object.keys(validationErrors).length > 0) {
                        setTouched(Object.keys(validationErrors).reduce((acc, fieldName) => ({
                            ...acc,
                            [fieldName]: true,
                        }), {}));
                    }
                    else {
                        handleSubmit();
                        close();
                    }
                }}/>
                </Dialog.Actions>
              </>);
        }}
        </Formik>
      </Dialog>
    </Portal>);
};
export const DeactivationDialog = ({ close, user, me, }) => {
    const [createNote] = useCreateUserEventMutation();
    const [updateUser] = useUpdateUserMutation();
    return (<Portal>
      <Dialog visible={true} onDismiss={() => close()} style={styles.deactivateDialog}>
        <Dialog.Title>{`Complete ${user?.firstName} ${user?.lastName}`}</Dialog.Title>
        <Dialog.Content>
          <Text style={styles.prompt}>Are you sure?</Text>
        </Dialog.Content>
        <Dialog.Actions style={styles.buttons}>
          <Button title="Cancel" onPress={() => close()}/>
          <Button title="Complete" onPress={async () => {
            const eventPayload = {
                eventType: UserEventType.deactivated,
                forUserId: user.id,
                byUserId: me.id,
                note: JSON.stringify({
                    endDate: format(new Date(), 'MM-dd-yyyy'), // deactivation date is right now
                }),
            };
            const userUpdatePayload = {
                programType: null,
            };
            createNote(eventPayload).then(() => updateUser({
                payload: userUpdatePayload,
                userId: user.id,
            }).finally(() => close()));
        }}/>
        </Dialog.Actions>
      </Dialog>
    </Portal>);
};
const styles = StyleSheet.create({
    activateDialog: {
        width: 500,
        height: 520,
        alignSelf: 'center',
    },
    deactivateDialog: {
        width: 500,
        height: 210,
        alignSelf: 'center',
    },
    prompt: {
        fontSize: 18,
    },
    buttons: {
        flex: 1,
        justifyContent: 'space-between',
    },
});
